import { useEffect, useMemo } from 'react';
import type { MyTransactionsRequest } from 'src/apis/capacity-management/use-my-transactions';
import type {
  ReshuffleActionsResponse,
  SearchAllocationsPayload,
} from 'src/apis/reshuffle-service/types';
import type { OptionalReshuffleServiceRootRequest } from 'src/apis/reshuffle-service/use-reshuffle-service';
import { getAllocationIds } from 'src/apis/reshuffle-service/utils';
import { useAxios } from 'src/hooks/use-axios';

type ExecuteProps = {
  url: string;
  payload: SearchAllocationsPayload;
};

export function useReshuffleAllocationActions(
  myTransactionsRequest: MyTransactionsRequest,
  reshuffleServiceRoot: OptionalReshuffleServiceRootRequest
) {
  const request = useAxios(
    (axios, baseConfig, { url, payload }: ExecuteProps) =>
      axios.request<ReshuffleActionsResponse>({
        ...baseConfig,
        url,
        method: 'POST',
        data: payload,
      }),
    { neededOnPageLoad: false }
  );

  const executeProps = useMemo(() => {
    const searchAllocations =
      reshuffleServiceRoot?.response?.data._links?.searchAllocations;
    const items = myTransactionsRequest.response?.data._embedded?.items;

    if (!searchAllocations || !items) {
      return;
    }

    const ids = items.flatMap(getAllocationIds);

    if (ids.length === 0) {
      return;
    }

    return { url: searchAllocations.href, payload: { ids } };
  }, [myTransactionsRequest.response, reshuffleServiceRoot?.response]);

  /* Refreshing the transactions table gives us a new response object, so the request is keyed on
  its content to not re-fetch the actions for allocations we already asked about. */
  const executeKey =
    executeProps && [executeProps.url, ...executeProps.payload.ids].join(',');

  useEffect(() => {
    if (!executeProps) {
      return;
    }

    request.execute(executeProps);
    // oxlint-disable-next-line exhaustive-deps
  }, [executeKey]);

  return request;
}

export type ReshuffleAllocationActionsRequest = ReturnType<
  typeof useReshuffleAllocationActions
>;
